import { z } from "zod";
import { HAZWOPER_OPTIONS, HOW_DID_YOU_FIND_OUT_ABOUT_BUXUS_OPTIONS, STAFFED_OPTIONS } from "./agencyConstants";
import { STAKEHOLDER_COMMANDER } from "./stakeholderConstants";
import { isLegacyPracticalPhone, LEGACY_PHONE_INVALID_MESSAGE } from "./phoneLegacy";

const PLACEHOLDER = "-1";

export function isPracticalWebsite(value: string): boolean {
  const raw = value.trim();
  if (!raw) return true;

  try {
    const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw);
    let urlToValidate: string;

    if (hasProtocol) {
      const protocolMatch = raw.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):\/\//);
      if (!protocolMatch) return false;
      const protocol = protocolMatch[1].toLowerCase();
      if (!["http", "https", "ftp", "ftps"].includes(protocol)) return false;
      urlToValidate = raw;
    } else {
      const domainPattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/.*)?$/;
      if (!domainPattern.test(raw)) return false;
      urlToValidate = `http://${raw}`;
    }

    const url = new URL(urlToValidate);
    if (!url.hostname) return false;
    const hostnamePattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return hostnamePattern.test(url.hostname);
  } catch {
    return false;
  }
}

function filteredAgencyTypes(types: string[]): string[] {
  return types.map((t) => t.trim()).filter((t) => t.length > 0 && t !== PLACEHOLDER);
}

export const registrationAgencyInfoFieldsSchema = z.object({
  typeOfAgency: z.array(z.string()),
  stationNameOrNumber: z.string().optional().default(""),
  nameOfAgencyCommandingOfficer: z.string().optional().default(""),
  nonEmergencyNumberForAgencyOrDepartment: z.string().optional().default(""),
  website: z.string().optional().default(""),
  staffed: z.string().optional().default(""),
  numberOfPeopleInDepartment: z.string().optional().default(""),
  /** Shown on Agency Info; CRM field names stay Dispatch_* for Zoho parity. */
  dispatchRadioSystemsUsedByAgency: z.array(z.string()).default([]),
  dispatchRadioSystemsUsedByAgencyOther: z.string().optional().default(""),
  dispatchIsRadioSystemP25Compliant: z.boolean().nullable().default(null),
  dispatchIsRadioSystemEncrypted: z.boolean().nullable().default(null),
});

export function registrationAgencyInfoStepSchema(stakeholderType: string) {
  const isCommander = stakeholderType === STAKEHOLDER_COMMANDER;

  return registrationAgencyInfoFieldsSchema.superRefine((v, ctx) => {
    if (filteredAgencyTypes(v.typeOfAgency).length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["typeOfAgency"],
        message: "Type of Agency is required",
      });
    }

    const nonEmergency = v.nonEmergencyNumberForAgencyOrDepartment?.trim() ?? "";
    if (!nonEmergency) {
      ctx.addIssue({
        code: "custom",
        path: ["nonEmergencyNumberForAgencyOrDepartment"],
        message: "Please specify a Non Emergency Number",
      });
    } else if (!isLegacyPracticalPhone(nonEmergency)) {
      ctx.addIssue({
        code: "custom",
        path: ["nonEmergencyNumberForAgencyOrDepartment"],
        message: LEGACY_PHONE_INVALID_MESSAGE,
      });
    }

    if (!isCommander && !(v.nameOfAgencyCommandingOfficer?.trim())) {
      ctx.addIssue({
        code: "custom",
        path: ["nameOfAgencyCommandingOfficer"],
        message: "Name of Commanding Officer or Superior is required",
      });
    }

    const staffed = v.staffed?.trim() ?? "";
    if (!staffed || staffed === PLACEHOLDER || !(STAFFED_OPTIONS as readonly string[]).includes(staffed)) {
      ctx.addIssue({
        code: "custom",
        path: ["staffed"],
        message: "Staffed is required",
      });
    }

    const headcount = v.numberOfPeopleInDepartment?.trim() ?? "";
    if (!headcount) {
      ctx.addIssue({
        code: "custom",
        path: ["numberOfPeopleInDepartment"],
        message: "Number of people in department is required",
      });
    }

    const website = v.website?.trim() ?? "";
    if (website && !isPracticalWebsite(website)) {
      ctx.addIssue({
        code: "custom",
        path: ["website"],
        message: "Please enter a valid URL",
      });
    }

    if (
      v.dispatchRadioSystemsUsedByAgency.includes("Other") &&
      !v.dispatchRadioSystemsUsedByAgencyOther.trim()
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["dispatchRadioSystemsUsedByAgencyOther"],
        message: "Please specify the radio system",
      });
    }

    if (v.dispatchIsRadioSystemP25Compliant === null) {
      ctx.addIssue({
        code: "custom",
        path: ["dispatchIsRadioSystemP25Compliant"],
        message: "Please indicate whether your radio system is P25 compliant",
      });
    }

    if (v.dispatchIsRadioSystemEncrypted === null) {
      ctx.addIssue({
        code: "custom",
        path: ["dispatchIsRadioSystemEncrypted"],
        message: "Please indicate whether your radio system is encrypted",
      });
    }
  });
}

export const registrationAgencyCapabilitiesStepSchema = z.object({
  canYourDepartmentProvide: z.array(z.string()).default([]),
  /** @deprecated Retired from registration UI — historic CRM Yes/No only (now on Can_your_department_provide). */
  provideMedicalAssistance: z.boolean().default(false),
  /** @deprecated Retired from registration UI — historic CRM Yes/No only (now on Can_your_department_provide). */
  assistInDirectingTraffic: z.boolean().default(false),
  /** @deprecated Retired from registration UI — historic CRM Yes/No only (now on Can_your_department_provide). */
  radioCommunication: z.boolean().default(false),
  /** @deprecated Retired from registration UI — historic CRM Yes/No only (Have_800_Trunk_radios); radio on Dispatch — do not add picklist option. */
  trunkRadios: z.boolean().default(false),
  /** @deprecated Retired from registration UI — historic CRM Yes/No only (now on Can_your_department_provide). */
  computerInternetAccess: z.boolean().default(false),
});

export const registrationAgencyProceduresStepSchema = z.object({
  naturalGasEmergencies: z.boolean().default(false),
  hazardousLiquidContingencyPlan: z.boolean().default(false),
  publicEvacuation: z.boolean().default(false),
});

export const registrationAgencyTrainingFieldsSchema = z.object({
  hazwoper: z.string().optional().default(""),
  naturalGasNfpa329: z.boolean().default(false),
  hazardousMaterials: z.boolean().default(false),
  hazardousLiquids: z.boolean().default(false),
  highlyVolatileLiquids: z.boolean().default(false),
  pipelineEmergencies: z.boolean().default(false),
  incidentCommand: z.boolean().default(false),
  nationalIncidentManagementSystem: z.boolean().default(false),
  dotEmergencyResponseGuide: z.boolean().default(false),
  emergencyMedicalTechnician: z.boolean().default(false),
});

export function registrationAgencyTrainingStepSchema() {
  return registrationAgencyTrainingFieldsSchema.superRefine((v, ctx) => {
    const hazwoper = v.hazwoper?.trim() ?? "";
    if (!hazwoper || hazwoper === PLACEHOLDER || !(HAZWOPER_OPTIONS as readonly string[]).includes(hazwoper)) {
      ctx.addIssue({
        code: "custom",
        path: ["hazwoper"],
        message: "HAZMAT is required",
      });
    }
  });
}

export const registrationMutualAidStepSchema = z.object({
  mutualAidAgreements: z.boolean().default(false),
  withWhom: z.string().optional().default(""),
});

export const registrationAdditionalQuestionsStepSchema = z
  .object({
    howDidYouFindOutAboutBuxus: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusAgency: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusAssociation: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusConference: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusOperator: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusTrainer: z.string().optional().default(""),
    howDidYouFindOutAboutBuxusWebinar: z.string().optional().default(""),
  })
  .superRefine((v, ctx) => {
    const source = v.howDidYouFindOutAboutBuxus.trim();
    if (
      !source ||
      !(HOW_DID_YOU_FIND_OUT_ABOUT_BUXUS_OPTIONS as readonly string[]).includes(source)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxus"],
        message: "Please select how you found out about Buxus®",
      });
    }
    if (source === "Agency" && !v.howDidYouFindOutAboutBuxusAgency.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusAgency"],
        message: "Please specify the agency",
      });
    }
    if (source === "Association" && !v.howDidYouFindOutAboutBuxusAssociation.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusAssociation"],
        message: "Please specify the association",
      });
    }
    if (source === "Conference" && !v.howDidYouFindOutAboutBuxusConference.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusConference"],
        message: "Please specify the conference",
      });
    }
    if (source === "Operator" && !v.howDidYouFindOutAboutBuxusOperator.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusOperator"],
        message: "Please specify the operator",
      });
    }
    if (source === "Training" && !v.howDidYouFindOutAboutBuxusTrainer.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusTrainer"],
        message: "Please specify the trainer",
      });
    }
    if (source === "Webinar" && !v.howDidYouFindOutAboutBuxusWebinar.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["howDidYouFindOutAboutBuxusWebinar"],
        message: "Please specify who hosted the webinar",
      });
    }
  });

export { registrationDispatchCapabilitiesStepSchema } from "./dispatchSchemas";

export function crmYesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

export { filteredAgencyTypes };
